// src/lib/effective-plan.ts
//
// Single source of truth for "what tier does this user effectively have?"
//
// Two sources of plan-state coexist on every User row:
//
//   1. subscriptionPlan + subscriptionStatus — Stripe-driven. Updated
//      by the Stripe webhook when a subscription becomes active,
//      cancels, or transitions to trialing.
//   2. compAccess + compExpiresAt — admin-granted. Set via the
//      POST /api/admin/users/:userId/comp-access endpoint to give
//      beta testers, family members, partners, etc. tier-level access
//      without faking a Stripe subscription.
//
// Both can be set on the same user. This module decides which wins.
// Every server-side surface that reads "user's plan" SHOULD call
// getEffectivePlan(user) instead of reading subscriptionPlan
// directly — otherwise comp-access grants don't propagate.

import type { PlanKey } from './plan-config'

/**
 * The fields getEffectivePlan reads. Defined as a structural type so
 * callers can pass any DB row, a partial User select, or a mocked
 * object — none of them need to be a full Prisma User instance.
 */
export type EffectivePlanInput = {
  subscriptionPlan: PlanKey | string
  subscriptionStatus: string | null
  compAccess: PlanKey | string | null
  compExpiresAt: Date | null
  /**
   * End of the period Stripe has billed for.
   *
   * REQUIRED, along with pastDueSince below. Both were optional, on the
   * reasoning that a caller omitting them "fails CLOSED, which is safe".
   *
   * That reasoning is inverted for a billing grace window. Failing closed here
   * does not protect anything — it locks out a customer who HAS paid, on the
   * day their card bounced, which is a refund and a one-star review rather than
   * a safe default. And it was not hypothetical: a survey of all 63 call sites
   * found that NOT ONE selected pastDueSince, so the seven-day grace this file
   * carefully computes had never once applied, on any surface, for any
   * customer.
   *
   * Required means the compiler now names every site that would silently
   * disable it — which is how a field added here reaches all of them instead of
   * the ones somebody remembered.
   */
  currentPeriodEnd: Date | null
  /** When the subscription first went `past_due` — the start of the dunning clock. */
  pastDueSince: Date | null
  /**
   * The user's school membership (at most one), with the license period.
   * Anyone in a school whose license is active — student or staff — has Pro.
   *
   * REQUIRED for the same reason as currentPeriodEnd: an optional field would
   * leave every call site that forgot to select it quietly treating school
   * students as Free. See src/lib/school/.
   */
  orgMemberships: { org: { startsAt: Date; expiresAt: Date | null } }[]
  /**
   * When the user's App Store subscription stops granting Pro (billing grace
   * included; null after a refund). Derived from AppStoreSubscription by
   * src/lib/billing/app-store/apply.ts.
   *
   * REQUIRED for the same reason as the fields above: a student who paid
   * through the App Store must be Pro on the website too, and an optional field
   * would leave every call site that forgot it treating them as Free — while
   * offering them a second, Stripe subscription for the thing they already own.
   */
  appStoreProUntil: Date | null
}

/**
 * How long a customer keeps their plan after a charge fails.
 *
 * Stripe smart-retries for two to three weeks, so this is deliberately SHORTER
 * than Stripe's own window: long enough to cover the overwhelmingly common case
 * (an expired card, noticed and fixed within a few days), short enough that a
 * genuinely lapsed customer is not carried for most of a month.
 *
 * The first version bounded grace by currentPeriodEnd alone. That was too
 * generous: when a RENEWAL invoice fails Stripe has already advanced the
 * period, so the bound sat a full cycle out — about 30 free days on one
 * declined card. Both bounds now apply, whichever expires first.
 */
export const DUNNING_GRACE_DAYS = 7

/**
 * The Prisma `select` every effective-plan read should use.
 *
 * getEffectivePlan silently degrades when a field is missing — most sharply
 * with currentPeriodEnd, where an absent value disables the dunning grace
 * window and the caller quietly reverts to the old lock-them-out behaviour.
 * Roughly twenty call sites hand-write this select; one constant means a field
 * added here reaches all of them instead of the ones somebody remembered.
 */
export const EFFECTIVE_PLAN_SELECT = {
  subscriptionPlan: true,
  subscriptionStatus: true,
  compAccess: true,
  compExpiresAt: true,
  currentPeriodEnd: true,
  pastDueSince: true,
  orgMemberships: { select: { org: { select: { startsAt: true, expiresAt: true } } }, take: 1 },
  appStoreProUntil: true,
} as const

/**
 * Whether a school license covers this user right now. Same rule as
 * licenseStatus() in src/lib/school/school-settings.ts: a license with no end
 * date covers nothing.
 */
export function hasActiveSchoolLicense(
  user: Pick<EffectivePlanInput, 'orgMemberships'>,
  nowMs: number = Date.now(),
): boolean {
  // `?? []`: the type requires the field, but a hand-built object from JSON
  // or a mocked row can still arrive without it; that must read as "no school",
  // not throw on every plan check.
  return (user.orgMemberships ?? []).some(
    ({ org }) =>
      org.expiresAt !== null && nowMs >= org.startsAt.getTime() && nowMs < org.expiresAt.getTime(),
  )
}

/**
 * Statuses where the customer is mid-dunning: they had a working subscription,
 * a charge failed, and Stripe is still retrying the card. NOT terminal.
 */
const DUNNING_STATUSES = new Set(['past_due'])

/**
 * Return the tier a user effectively has access to.
 *
 * Precedence (highest first):
 *   0. Active school license — the user belongs to a school whose license
 *      period includes now. Returns PRO (the school pays; there is no higher
 *      tier, so nothing below can outrank it).
 *   1. Active comp access — compAccess is set AND (compExpiresAt is
 *      null OR in the future). Returns compAccess.
 *   2. Active Stripe subscription — subscriptionStatus is 'active' or
 *      'trialing'. Returns subscriptionPlan.
 *   2b. Active App Store subscription — appStoreProUntil is in the future.
 *      Returns PRO.
 *   3. Dunning grace — subscriptionStatus is 'past_due' and currentPeriodEnd
 *      is still in the future. Returns subscriptionPlan: a failed charge that
 *      Stripe is still retrying is not a cancellation.
 *   4. Otherwise — FREE.
 *
 * WARNING — "demote to FREE" code paths: because comp wins (step 1), setting
 * subscriptionPlan='FREE' alone is a SILENT NO-OP for a comp user — they keep
 * their comp tier until compAccess (+ the comp* metadata) is ALSO cleared. A
 * real downgrade must clear compAccess AND subscriptionStatus AND
 * subscriptionPlan together. (Stripe-webhook cancellation is the deliberate
 * exception: it leaves comp alone — comp is a separate, admin-granted axis.)
 * Full spec: docs/billing.md → "Effective plan: comp access overrides subscriptionPlan".
 *
 * `nowMs` is injectable so unit tests can pin a fixed time without
 * having to mock Date.
 */
export function getEffectivePlan(user: EffectivePlanInput, nowMs: number = Date.now()): PlanKey {
  // 0. A school license covers everyone in the school while it runs. When it
  //    ends the student keeps their account and falls back to whatever else
  //    they have — usually Free.
  if (hasActiveSchoolLicense(user, nowMs)) return 'PRO'

  // 1. Comp access wins when set and not expired.
  if (user.compAccess) {
    const compExpired = user.compExpiresAt !== null && user.compExpiresAt.getTime() <= nowMs
    if (!compExpired) {
      return coercePlan(user.compAccess)
    }
  }

  // 2. Otherwise honor the Stripe subscription, but only if it's
  //    actually active. A user whose subscription has been cancelled
  //    or whose payment failed (subscriptionStatus null / past_due /
  //    unpaid) falls back to FREE — we don't grant tier access on
  //    the lingering subscriptionPlan column alone.
  const isActiveSub = user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing'
  if (isActiveSub) {
    return coercePlan(user.subscriptionPlan)
  }

  // 2b. An App Store subscription. Apple's own billing grace is already folded
  //     into appStoreProUntil, and a refund has already cleared it, so this is
  //     one comparison. The App Store only sells Pro.
  if (hasActiveAppStoreSubscription(user, nowMs)) return 'PRO'

  // 3. DUNNING GRACE. A failed charge is not a cancellation.
  //
  // Stripe smart-retries a declined card for roughly two to three weeks before
  // giving up and moving the subscription to `unpaid`/`canceled`. Revoking on
  // the FIRST failure locks out a customer Stripe is still actively trying to
  // charge — usually for something as dull as an expired card.
  //
  // This is not hypothetical. A Pro customer who paid on 2026-07-09 had her
  // renewal fail on 2026-08-09 and lost access the same minute, inside a period
  // ending 2026-09-09, while being shown "Upgrade to Pro" as though she had
  // never paid — and no dunning email reached her, so she had no way to know a
  // card was the problem.
  //
  // Bounded TWICE, whichever expires first: DUNNING_GRACE_DAYS from the first
  // failure, and currentPeriodEnd. The period bound alone was too generous —
  // a failed RENEWAL has already advanced the period, so it sat a full cycle
  // out. In practice Stripe often ends it sooner still by moving to a terminal
  // status, which falls through to FREE below. Grace is deliberately NOT extended to `unpaid` or `incomplete` —
  // `unpaid` means Stripe has given up, and `incomplete` means the very first
  // payment never succeeded, so there is no established customer to protect.
  if (
    user.subscriptionStatus !== null &&
    DUNNING_STATUSES.has(user.subscriptionStatus) &&
    // The dunning clock must have actually started. Null = unknown = no grace.
    user.pastDueSince != null &&
    nowMs < user.pastDueSince.getTime() + DUNNING_GRACE_DAYS * 24 * 60 * 60 * 1000 &&
    // AND still inside the period Stripe billed for — whichever bound expires
    // first wins, so neither alone can hand out an unbounded free ride.
    user.currentPeriodEnd != null &&
    user.currentPeriodEnd.getTime() > nowMs
  ) {
    return coercePlan(user.subscriptionPlan)
  }

  // 4. Free by default.
  return 'FREE'
}

/**
 * Canonical "is this a paid user" check (comp-aware). Use this for entitlement
 * gates instead of the comp-blind `session.user.plan`. Packaging v2: PRO is the
 * only paid tier (legacy PREMIUM is coerced to PRO upstream).
 */
export function isPaidUser(user: EffectivePlanInput, nowMs: number = Date.now()): boolean {
  return getEffectivePlan(user, nowMs) === 'PRO'
}

/** Whether an App Store subscription grants Pro right now. */
export function hasActiveAppStoreSubscription(
  user: Pick<EffectivePlanInput, 'appStoreProUntil'>,
  nowMs: number = Date.now(),
): boolean {
  return user.appStoreProUntil != null && user.appStoreProUntil.getTime() > nowMs
}

/**
 * WHERE a user's Pro comes from — the first source, in getEffectivePlan's
 * precedence order, that grants it. Null for a Free user.
 *
 * Surfaces need this to send someone to the right place: an App Store
 * subscriber manages billing in iPhone Settings and must never be offered
 * Stripe checkout (they would pay twice); a web subscriber has no App Store
 * subscription to restore or manage; a school or comp user has nothing to
 * manage at all.
 *
 * Kept in lockstep with getEffectivePlan by a test that asserts the two agree
 * on paid-vs-free for every combination of sources.
 */
export type PlanSource = 'school' | 'comp' | 'stripe' | 'app_store'

export function getPlanSource(
  user: EffectivePlanInput,
  nowMs: number = Date.now(),
): PlanSource | null {
  if (getEffectivePlan(user, nowMs) !== 'PRO') return null
  if (hasActiveSchoolLicense(user, nowMs)) return 'school'
  if (user.compAccess && coercePlan(user.compAccess) === 'PRO' && isCompAccessActive(user, nowMs)) {
    return 'comp'
  }
  const isActiveSub = user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing'
  if (isActiveSub && coercePlan(user.subscriptionPlan) === 'PRO') return 'stripe'
  if (hasActiveAppStoreSubscription(user, nowMs)) return 'app_store'
  // The only remaining way to be PRO is Stripe's dunning grace.
  return 'stripe'
}

/**
 * Coerce a raw DB plan string into the narrowed PlanKey union.
 *
 * The Prisma SubscriptionPlan enum still includes BASIC for
 * historical-data compatibility, but the application no longer
 * exposes BASIC as a sellable tier (retired in #420). Any row that
 * still has 'BASIC' is mapped to FREE here — the safest default,
 * matching the user's actual access level (BASIC tier was retired,
 * grandfathered users were migrated to other tiers manually).
 *
 * Defined inline (not exported) so callers must go through
 * getEffectivePlan and we have ONE place that knows about the
 * legacy value. If we ever need to expose the coercion to other
 * call sites, export it — but the goal is to keep the BASIC
 * special-case contained.
 */
export function coercePlan(raw: PlanKey | string): PlanKey {
  if (raw === 'PRO' || raw === 'FREE') return raw
  // Packaging v2: PREMIUM was retired as a sellable tier → grandfather any
  // PREMIUM row (DB subscriptionPlan or comp grant) to PRO.
  if (raw === 'PREMIUM') return 'PRO'
  // Anything else (including legacy 'BASIC') → FREE.
  return 'FREE'
}

/**
 * Whether a user's comp access is currently effective (set and not
 * expired). Independent of whether comp is the one that wins (e.g.
 * a user could have BOTH active Stripe Premium AND comp Pro — comp
 * still wins, but isCompAccessActive lets a UI surface that fact).
 *
 * Used by admin UIs to show "comp active until X" alongside the
 * Stripe state, and by audit logs to record whether a grant is in
 * force.
 */
export function isCompAccessActive(
  user: Pick<EffectivePlanInput, 'compAccess' | 'compExpiresAt'>,
  nowMs: number = Date.now(),
): boolean {
  if (!user.compAccess) return false
  if (user.compExpiresAt === null) return true
  return user.compExpiresAt.getTime() > nowMs
}
